import { useMemo } from 'react';
import { ReactComponent as CriticalAssetTagColor } from '@elementary/common-ui/assets/16x16/critical-asset-tag-color.svg';
import { ReactComponent as DownStreamArrow } from '@elementary/common-ui/assets/16x16/down-stream.svg';
import { ReactComponent as UpStreamArrow } from '@elementary/common-ui/assets/16x16/up-stream.svg';
import {
  AvatarGroup,
  ChipCounterList,
  FlexCenter,
  FlexSpace,
  OverflowTooltip,
  RestrictedIndicator,
} from '@elementary/common-ui/components';
import { ChipStatus } from '@elementary/common-ui/components/Chip/StatusChip';
import { Column, ColumnType } from '@elementary/common-ui/components/Table';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import {
  capitalize,
  getTreeTypeByDisplayType,
} from '@elementary/common-ui/utils/common';
import dateComparer from '@elementary/common-ui/utils/comparers/dateComparer';
import { Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import LastModelRunState from 'catalog/components/LastModelRunState';
import { getTestResultsTotalsColumnFilterConfig } from '@elementary/common-ui/utils/testResultsTotals';
import { Totals } from '@elementary/common-ui/types';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import TestResultsTableCell from 'shared/cellRenderers/TestResultsTableCell';
import {
  compareByTestResultsTotals,
  getTestResultsGroup,
  getTestExecutionTotals,
} from 'shared/utils/testExecutions';
import { space } from '@elementary/common-ui/style/utils';
import { NormalizedDependency } from './useDependenciesData';

const useDependenciesColumns = (
  resourceType: string,
  data: NormalizedDependency[] = [],
) => {
  const columnType = resourceType === 'COLUMN';
  const { setSelectedTreeModel } = useSelectedTreeItem();
  const hasPermission = useEnvPermission();

  const testResultsTotalsMap = useMemo(() => {
    const map: Record<string, Totals> = {};
    data.forEach((dep) => {
      if (dep.testExecutionsTotals) {
        map[dep.id] = dep.testExecutionsTotals;
      } else if (dep.latestTestExecutions?.length) {
        map[dep.id] = getTestExecutionTotals(dep.latestTestExecutions);
      }
    });
    return map;
  }, [data]);

  const columnArr: Array<Column<NormalizedDependency>> = [
    {
      id: 'direction',
      header: 'Direction',
      renderer: ({ direction }) => (
        <FlexCenter sx={{ gap: 1 }}>
          <FlexCenter>
            {direction === 'UPSTREAM' ? <UpStreamArrow /> : <DownStreamArrow />}
          </FlexCenter>
          <OverflowTooltip>
            {capitalize(direction.toLowerCase())}
          </OverflowTooltip>
        </FlexCenter>
      ),
      nonToggleable: true,
    },
    {
      id: 'hops',
      header: 'Hops',
      width: 120,
    },
    {
      id: 'name',
      header: 'Name',
      showValueWhenEmpty: true,
      renderer: (val) => {
        if (val.isRestricted) {
          return <RestrictedIndicator />;
        }
        const hasLink = !!val.path;
        return (
          <FlexSpace sx={{ gap: space('xs') }}>
            <OverflowTooltip
              typographySx={
                hasLink
                  ? { color: 'accent.primary', cursor: 'pointer' }
                  : undefined
              }
              onClick={
                hasLink
                  ? () => {
                      const treeType = getTreeTypeByDisplayType(val.type);
                      setSelectedTreeModel(val.id, undefined, treeType);
                    }
                  : undefined
              }
            >
              {val.name}
            </OverflowTooltip>

            {!!val.isCritical && (
              <Tooltip title="Critical Asset">
                <FlexCenter>
                  <CriticalAssetTagColor />
                </FlexCenter>
              </Tooltip>
            )}
          </FlexSpace>
        );
      },
    },
    {
      id: 'type',
      header: 'Type',
      renderer: (val) => (
        <OverflowTooltip>{capitalize(val.type)}</OverflowTooltip>
      ),
    },
    {
      id: 'columnName',
      header: 'Column Name',
      hideColumn: !columnType,
    },
    {
      id: 'path',
      header: 'Path',
      renderer: (val) => <OverflowTooltip>{val.path}</OverflowTooltip>,
      defaultUntoggled: true,
    },
    {
      id: 'owners',
      header: 'Owner',
      width: 160,
      renderer: ({ owners }) => <AvatarGroup items={owners} />,
    },
    {
      id: 'tags',
      header: 'Tags',
      width: 160,
      renderer: ({ tags }) => <ChipCounterList items={tags} />,
      defaultUntoggled: true,
    },
    {
      id: 'id',
      header: 'Test results',
      ...getTestResultsTotalsColumnFilterConfig({
        testResultsTotals: testResultsTotalsMap,
        key: 'id',
      }),
      sortable: true,
      compareFunc: (a, b) =>
        compareByTestResultsTotals(
          testResultsTotalsMap[a.id],
          testResultsTotalsMap[b.id],
        ),
      renderer: ({ id, latestTestExecutions }) => (
        <TestResultsTableCell
          assetId={id}
          statuses={getTestResultsGroup(latestTestExecutions)}
        />
      ),
      hideColumn: !hasPermission('VIEW_TESTS'),
    },
    {
      id: 'latestExecutionTimestamp',
      header: 'Last run at',
      type: ColumnType.Date,
      compareFunc: (a, b) =>
        dateComparer(a.latestExecutionTimestamp, b.latestExecutionTimestamp),
      filterValueMatcher: ([from, to, emptyFlag], rowData) => {
        const time = rowData.latestExecutionTimestamp;

        if (emptyFlag === 'true') return !time;
        if (!time) return false;

        const timeDayjs = dayjs(time);

        return (
          (!from || dayjs(from).startOf('day').isBefore(timeDayjs)) &&
          (!to || dayjs(to).endOf('day').isAfter(timeDayjs))
        );
      },
      renderer: (val) => (
        <LastModelRunState
          status={
            val.latestExecutionStatus
              ? (val.latestExecutionStatus as ChipStatus)
              : undefined
          }
          time={val.latestExecutionTimestamp}
          showOnlyTime
        />
      ),
      defaultUntoggled: true,
    },
    {
      id: 'latestExecutionStatus',
      header: 'Last run status',
      renderer: (val) => (
        <LastModelRunState
          status={
            val.latestExecutionStatus
              ? (val.latestExecutionStatus as ChipStatus)
              : undefined
          }
          showOnlyStatus
        />
      ),
      filterable: true,
      filterOperators: ['equals', 'notEquals'],
      filterOptionsOverride: [
        { value: 'success', label: 'Success' },
        { value: 'skipped', label: 'Skipped' },
        { value: 'error', label: 'Error' },
        { value: '', label: '' },
      ],
      defaultUntoggled: true,
    },
  ];

  return { columnArr };
};

export default useDependenciesColumns;
